import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ReactNode } from 'react';
import { useDropdownPosition } from '../../hooks/useDropdownPosition';
import { FormField } from './FormField';
import styles from './Select.module.scss';

// ============================================
// TYPES
// ============================================

export type InputSize = 'sm' | 'md' | 'lg';

export interface SelectOption {
  label: string;
  value: string;
  icon?: ReactNode;
  description?: string;
  disabled?: boolean;
}

export interface SelectProps {
  options: SelectOption[];
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  label?: string;
  isRequired?: boolean;
  error?: string;
  helperText?: string;
  disabled?: boolean;
  loading?: boolean;
  searchable?: boolean;
  size?: InputSize;
  fullWidth?: boolean;
  id?: string;
  noResultsText?: string;
}

// ============================================
// ICONS
// ============================================

function ChevronIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M4 6l4 4 4-4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SpinnerIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="2" opacity="0.25" />
      <path
        d="M14 8a6 6 0 0 0-6-6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

// ============================================
// COMPONENT
// ============================================

function Select({
  options,
  value,
  onChange,
  placeholder = 'Select item',
  label,
  isRequired = false,
  error,
  helperText,
  disabled = false,
  loading = false,
  searchable = false,
  size = 'md',
  fullWidth = true,
  id: idProp,
  noResultsText = 'No results found',
}: SelectProps) {
  const autoId = useId();
  const id = idProp ?? autoId;
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const optionRefs = useRef<(HTMLDivElement | null)[]>([]);
  const handleClose = useCallback(() => {
    setIsOpen(false);
    setSearch('');
    setHighlightedIndex(-1);
  }, []);
  const dropdownPosition = useDropdownPosition(wrapperRef, isOpen, handleClose);

  const selectedOption = options.find((o) => o.value === value);

  const filteredOptions = useMemo(
    () =>
      searchable && search
        ? options.filter((o) => o.label.toLowerCase().includes(search.toLowerCase()))
        : options,
    [options, searchable, search],
  );

  // Reset highlighted index when search text changes
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [search]);

  // Reset highlighted index when dropdown closes
  useEffect(() => {
    if (!isOpen) {
      setHighlightedIndex(-1);
    }
  }, [isOpen]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      const insideWrapper = wrapperRef.current?.contains(target);
      const insideDropdown = dropdownRef.current?.contains(target);
      if (!insideWrapper && !insideDropdown) {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  // Focus search input when opened
  useEffect(() => {
    if (isOpen && searchable && searchRef.current) {
      searchRef.current.focus();
    }
  }, [isOpen, searchable]);

  // Scroll highlighted option into view
  useEffect(() => {
    if (highlightedIndex >= 0) {
      const el = optionRefs.current[highlightedIndex];
      if (el) {
        el.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex]);

  const handleToggle = () => {
    if (disabled || loading) return;
    setIsOpen((prev) => !prev);
    if (isOpen) setSearch('');
  };

  const handleSelect = (option: SelectOption) => {
    if (option.disabled) return;
    onChange(option.value);
    setIsOpen(false);
    setSearch('');
  };

  /**
   * Find the next non-disabled option index starting from `from` in the given direction.
   * Returns -1 if no valid option is found (all disabled or empty list).
   */
  const findNextEnabledIndex = useCallback(
    (from: number, direction: 1 | -1): number => {
      const count = filteredOptions.length;
      if (count === 0) return -1;
      let idx = from;
      for (let i = 0; i < count; i++) {
        idx = (idx + direction + count) % count;
        const opt = filteredOptions[idx];
        if (opt && !opt.disabled) return idx;
      }
      return -1;
    },
    [filteredOptions],
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      setIsOpen(false);
      setSearch('');
      return;
    }

    if (!isOpen) {
      // Open dropdown on arrow down/up or Enter when closed
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter') {
        e.preventDefault();
        if (!disabled && !loading) {
          setIsOpen(true);
        }
      }
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      let nextIdx = findNextEnabledIndex(highlightedIndex, 1);
      // Skip already-selected item on first arrow press so highlight is visible
      if (highlightedIndex === -1 && nextIdx !== -1) {
        const opt = filteredOptions[nextIdx];
        if (opt && opt.value === value) {
          const skipIdx = findNextEnabledIndex(nextIdx, 1);
          if (skipIdx !== -1 && skipIdx !== nextIdx) nextIdx = skipIdx;
        }
      }
      if (nextIdx !== -1) setHighlightedIndex(nextIdx);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      let prevIdx = findNextEnabledIndex(
        highlightedIndex === -1 ? 0 : highlightedIndex,
        -1,
      );
      // Skip already-selected item on first arrow press so highlight is visible
      if (highlightedIndex === -1 && prevIdx !== -1) {
        const opt = filteredOptions[prevIdx];
        if (opt && opt.value === value) {
          const skipIdx = findNextEnabledIndex(prevIdx, -1);
          if (skipIdx !== -1 && skipIdx !== prevIdx) prevIdx = skipIdx;
        }
      }
      if (prevIdx !== -1) setHighlightedIndex(prevIdx);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightedIndex >= 0) {
        const opt = filteredOptions[highlightedIndex];
        if (opt && !opt.disabled) {
          handleSelect(opt);
        }
      }
    }
  };

  const triggerClasses = [
    styles.trigger,
    isOpen && styles.triggerOpen,
    error && styles.triggerError,
    disabled && styles.triggerDisabled,
  ]
    .filter(Boolean)
    .join(' ');

  const chevronClasses = [styles.chevron, isOpen && styles.chevronOpen].filter(Boolean).join(' ');

  const sizeClass = styles[size] ?? styles.md;
  const wrapperClasses = [styles.wrapper, sizeClass, fullWidth && styles.fullWidth]
    .filter(Boolean)
    .join(' ');

  return (
    <FormField
      id={id}
      label={label}
      isRequired={isRequired}
      helperText={helperText}
      error={error}
      isDisabled={disabled}
    >
      <div ref={wrapperRef} className={wrapperClasses} onKeyDown={handleKeyDown}>
        <div
          className={triggerClasses}
          onClick={handleToggle}
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-invalid={Boolean(error) || undefined}
          aria-required={isRequired || undefined}
          aria-disabled={disabled || undefined}
          tabIndex={disabled ? -1 : 0}
        >
          {searchable && isOpen ? (
            <input
              ref={searchRef}
              className={styles.searchInput}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={selectedOption?.label ?? placeholder}
              onClick={(e) => e.stopPropagation()}
            />
          ) : selectedOption ? (
            <span className={styles.selectedValue}>
              {selectedOption.icon && (
                <span className={styles.optionIcon}>{selectedOption.icon}</span>
              )}
              {selectedOption.label}
            </span>
          ) : (
            <span className={styles.placeholder}>{placeholder}</span>
          )}

          {loading ? (
            <span className={styles.spinner}>
              <SpinnerIcon />
            </span>
          ) : (
            <span
              className={chevronClasses}
              onClick={(e) => {
                e.stopPropagation();
                handleToggle();
              }}
            >
              <ChevronIcon />
            </span>
          )}
        </div>

      </div>

      {isOpen &&
        dropdownPosition &&
        createPortal(
          <div
            ref={dropdownRef}
            className={styles.dropdown}
            role="listbox"
            style={{
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              width: dropdownPosition.width,
              transform:
                dropdownPosition.placement === 'top' ? 'translateY(-100%)' : undefined,
            }}
          >
            {filteredOptions.length === 0 ? (
              <div className={styles.noResults}>{noResultsText}</div>
            ) : (
              filteredOptions.map((option, index) => {
                const isSelected = option.value === value;
                const isHighlighted = index === highlightedIndex;
                const optionClasses = [
                  styles.option,
                  isSelected && styles.optionSelected,
                  isHighlighted && styles.optionHighlighted,
                  option.disabled && styles.optionDisabled,
                ]
                  .filter(Boolean)
                  .join(' ');

                return (
                  <div
                    key={option.value}
                    ref={(el) => {
                      optionRefs.current[index] = el;
                    }}
                    className={optionClasses}
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => handleSelect(option)}
                    onMouseEnter={() => setHighlightedIndex(index)}
                  >
                    {option.icon && <span className={styles.optionIcon}>{option.icon}</span>}
                    {option.description ? (
                      <div className={styles.optionContent}>
                        <span className={styles.optionLabel}>{option.label}</span>
                        <span className={styles.optionDescription}>{option.description}</span>
                      </div>
                    ) : (
                      <span className={styles.optionLabel}>{option.label}</span>
                    )}
                  </div>
                );
              })
            )}
          </div>,
          document.body,
        )}
    </FormField>
  );
}

export default Select;
